import { skipKycDocument } from '@basqet-dashboard/common/api';
import { deleteKyc } from '@basqet-dashboard/common/api/kyc';
import { Button, Modal } from '@basqet-dashboard/common/components';
import { queryClient } from '@basqet-dashboard/common/config';
import { formatErrorMessage, isDocumentMandatory } from '@basqet-dashboard/common/utils';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { FilesToBeUploaded } from '../components';
import { KycDeclined } from '../components/kyc-declined/kyc-declined';
import { KycForm } from '../components/kyc-form';
import { KycPending } from '../components/kyc-pending';
import { KycSuccess } from '../components/kyc-success';

const ProcessKyc = ({ docsUploaded, documentsForKycType, meta, refetchKycInfo }) => {
  const { status } = meta;

  const handleSkip = async (documentTitle) => {
    try {
      await skipKycDocument(documentTitle);
      toast.success('Document skipped successfully');
      await refetchKycInfo();
    } catch (error) {
      formatErrorMessage(error);
    }
  };
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleRestartKyc = async () => {
    setIsDeleting(true);
    try {
      await deleteKyc();
      await queryClient.invalidateQueries('getKycInfo');
      await refetchKycInfo();
      toast.success('KYC process restarted successfully');
      setShowConfirmDialog(false);
    } catch (error) {
      formatErrorMessage(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const uploadedFiles = docsUploaded.map((file) => file.document_title);
  const skippedFiles = docsUploaded
    .filter((file) => file.status === 'SKIPPED')
    .map((file) => file.document_title);

  const sortedFilesList = documentsForKycType
    .map((file) => {
      const checked = uploadedFiles.includes(file.name);
      const isSkipped = skippedFiles.includes(file.name);
      const isMandatory = isDocumentMandatory(file);
      return {
        checked,
        isSkipped,
        isMandatory,
        value: file.name,
        ...file
      };
    })
    .sort((a, b) => {
      return b.checked - a.checked;
    });

  const fileToUpload = sortedFilesList.find((file) => !file.checked && !file.isSkipped);

  const KycStatusComponent = statuses[status];

  const canRestart = status !== 'APPROVED';

  const renderRestartSection = () => {
    if (!canRestart) return null;

    return (
      <>
        <div className="kyc-restart-section">
          <Button variant="white" size="small" onClick={() => setShowConfirmDialog(true)}>
            Restart KYC Process
          </Button>
        </div>

        {showConfirmDialog && (
          <Modal
            onModalClose={() => setShowConfirmDialog(false)}
            header={{ name: 'Restart KYC', cursive: 'Process' }}>
            <div className="kyc-restart-modal">
              <div className="kyc-restart-modal__message">
                <p>
                  <strong>Are you sure you want to restart the KYC process?</strong>
                </p>
                <p>This will delete all uploaded documents and you&apos;ll need to start over.</p>
              </div>
              <div className="kyc-restart-modal__actions">
                <Button variant="outline" onClick={() => setShowConfirmDialog(false)} isFull>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleRestartKyc} isLoading={isDeleting} isFull>
                  Yes, Restart KYC
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </>
    );
  };

  if (status !== 'IN_PROGRESS') {
    return (
      <>
        {renderRestartSection()}
        <KycStatusComponent files={docsUploaded} />
      </>
    );
  } else {
    return (
      <>
        {renderRestartSection()}
        <div className="kyc">
          <FilesToBeUploaded filesList={sortedFilesList} />
          {fileToUpload && (
            <KycForm
              title="freelancer"
              fileToUpload={fileToUpload?.value}
              fileInfo={fileToUpload}
              refetchKycInfo={refetchKycInfo}
              fileDocument={fileToUpload}
              onSkip={handleSkip}
            />
          )}
        </div>
      </>
    );
  }
};

const statuses = {
  PENDING: KycPending,
  APPROVED: KycSuccess,
  DECLINED: KycDeclined
};
ProcessKyc.propTypes = {
  docsUploaded: PropTypes.array,
  documentsForKycType: PropTypes.array,
  meta: PropTypes.object,
  refetchKycInfo: PropTypes.func.isRequired
};
export { ProcessKyc };
